// trial-store.js — the 7-day free trial's state and message schedule
// (Brief 016). A port of iOS `KFE7DaysTrialMgr` (`KFEModules/7-daysTrial/`).
//
// Rule #1 hygiene: no DOM, no imports from src/screens/ or src/ui/. The
// decision of WHICH message is due is a pure function of stored state plus a
// clock (`decideTrialMessage`); rendering it is the app shell's job.
//
// ---------------------------------------------------------------------------
// WHAT THE SERVER OWNS, AND WHAT THIS MODULE OWNS
// ---------------------------------------------------------------------------
//
// Brief 016 is explicit: **the licence logic lives on the CRM and this adds
// none of it.** Nothing here decides whether a learner is entitled to the
// course. Trial state arrives from the server — on the login / activate-trial
// response and then on every touch-base — and this module does exactly two
// things with it:
//
//   1. remembers it, because the messages below are scheduled against it; and
//   2. decides which of iOS's three trial messages is due right now.
//
// There is no local timer and no local grant: a learner whose trial the server
// has ended stops being a trial user the moment a touch-base says so
// (`applyTouchBaseTrialState`), exactly as iOS's hourly ping does.
//
// ---------------------------------------------------------------------------
// THE iOS STATE, FIELD FOR FIELD
// ---------------------------------------------------------------------------
//
// iOS keeps this in its own archived dictionary (`7DaysTrialContextFile.data`),
// NOT in the session — it outlives a login, and `setIs7DaysTrialWithServerMessage`
// rewrites it from the server's response on every login. This module is that
// file, in localStorage, with iOS's keys:
//
//   | iOS key                  | here                   | source field               |
//   |--------------------------|------------------------|----------------------------|
//   | `Is7DaysTrial`           | `isTrialUser`          | `is_trial_user`            |
//   | `AppDeactivateDate`      | `appDeactivateDate`    | `app_deactivate_date`      |
//   | `AppDeleteProgressDate`  | `appDeleteProgressDate`| `app_delete_progress_date` |
//   | `Is7DaysFinished`        | `isFinished`           | derived (deactivate ≤ now) |
//   | `LastCheckTimeStamp`     | `lastCheckAt`          | derived (set when shown)   |
//
// **THE TRIAL END IS `app_deactivate_date`, NOT `license_end_date`.** Brief 016
// §Scope item 5 and criterion 2 both name `license_end_date`; iOS reads
// `app_deactivate_date` (`KFE7DaysTrialMgr.m:63-77`) and never looks at
// `license_end_date` for the trial at all — `license_end_date` is the *paid*
// licence's end, which `KFELoginManager.m:182` writes to a different row for a
// different purpose. Rule #9 makes the phone canon, so the schedule below is
// driven by `app_deactivate_date` and the divergence from the brief's wording
// is recorded in docs/016-handover.md rather than resolved quietly. Both
// fields are stored, so a ruling the other way is a one-line change.
//
// ---------------------------------------------------------------------------
// WHAT IS DELIBERATELY NOT MIRRORED
// ---------------------------------------------------------------------------
//
// iOS's `appDidActivate` also enforces two LOCAL rules once the trial is over:
// it disables the app, and after `kDateRemoveProgressData` (10) days it runs
// `removeUserProgrssData` — `delete from app_item_info`, i.e. it destroys the
// learner's progress. **Neither is ported**, per Brief 016 §"What this is — and
// is not": they exist because the phone works offline and has to decide for
// itself, while the web asks the server on every heartbeat. The web has no
// business deleting a learner's progress on a local clock, and it never does.
//
// `endingOf7days`' OK button also deactivates the device and returns to the
// login page on iOS. The web offers the store page instead (Brief 016 §3), and
// that divergence is recorded in the handover.

/** localStorage key — iOS's `7DaysTrialContextFile.data`, in one row. */
export const TRIAL_CONTEXT_KEY = "kfe_trial_context";

/**
 * @typedef {object} TrialContext
 * @property {boolean} isTrialUser          iOS `Is7DaysTrial`
 * @property {number}  appDeactivateDate    unix SECONDS; 0 when not on trial
 * @property {number}  appDeleteProgressDate unix SECONDS; stored, never acted on
 * @property {boolean} isFinished           iOS `Is7DaysFinished`
 * @property {number}  lastCheckAt          unix SECONDS; 0 = never shown a message
 * @property {string}  [licenseEndDate]     the PAID licence's end, carried for the record
 */

/** The shape a learner who is not on trial has. iOS's `else` branch, verbatim. */
const EMPTY = /** @type {TrialContext} */ ({
  isTrialUser: false,
  appDeactivateDate: 0,
  appDeleteProgressDate: 0,
  isFinished: false,
  lastCheckAt: 0,
});

/** @returns {TrialContext} */
export function getTrialContext() {
  try {
    const raw = localStorage.getItem(TRIAL_CONTEXT_KEY);
    if (!raw) return { ...EMPTY };
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return { ...EMPTY };
    return {
      isTrialUser: parsed.isTrialUser === true,
      appDeactivateDate: num(parsed.appDeactivateDate),
      appDeleteProgressDate: num(parsed.appDeleteProgressDate),
      isFinished: parsed.isFinished === true,
      lastCheckAt: num(parsed.lastCheckAt),
      licenseEndDate: typeof parsed.licenseEndDate === "string" ? parsed.licenseEndDate : undefined,
    };
  } catch {
    return { ...EMPTY };
  }
}

/** @param {TrialContext} ctx */
function writeTrialContext(ctx) {
  try { localStorage.setItem(TRIAL_CONTEXT_KEY, JSON.stringify(ctx)); }
  catch { /* private mode etc. — the learner just sees no trial message */ }
}

/** Clear the trial context entirely. Used by tests and by a full reset. */
export function clearTrialContext() {
  try { localStorage.removeItem(TRIAL_CONTEXT_KEY); }
  catch { /* no-op */ }
}

/**
 * Coerce a server timestamp to unix seconds, tolerating the string form.
 * Returns 0 for anything unusable, which is iOS's `doubleValue` on a nil or
 * non-numeric object.
 * @param {any} v
 * @returns {number}
 */
function num(v) {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * `+[KFE7DaysTrialMgr setIs7DaysTrialWithServerMessage:]`
 * (KFE7DaysTrialMgr.m:33-88), ported.
 *
 * Called with the `data` block of a SUCCESSFUL login or activate-trial
 * response — iOS calls it from `KFELoginManager.analysisWithisSuccess`
 * (KFELoginManager.m:190), which is the one path BOTH the normal login and
 * the free-trial screen's `activateTrial` go through. So both callers here
 * must call it too, or a trial started on the trial screen would have no
 * schedule.
 *
 * Mirrors iOS's two branches exactly:
 *   • `is_trial_user` true  → store the two dates and derive `isFinished`
 *     from whether `app_deactivate_date` has already passed;
 *   • false                 → zero the dates and clear `isFinished`.
 *
 * `lastCheckAt` is deliberately PRESERVED across this call, because iOS
 * preserves it: `temContextDic` starts as a mutable copy of the existing
 * dictionary and `kLastCheckTimeStampKey` is never reset in either branch.
 * That is what keeps the welcome message a once-ever event rather than
 * something a re-login replays.
 *
 * @param {any} data the response's `data` block
 * @param {{ now?: number }} [opts] `now` in MILLISECONDS (client clock)
 * @returns {TrialContext} the context as written
 */
export function setTrialContextFromLogin(data, { now = Date.now() } = {}) {
  const previous = getTrialContext();
  const isTrialUser = truthy(data?.is_trial_user);
  const licenseEndDate = typeof data?.license_end_date === "string" ? data.license_end_date : previous.licenseEndDate;

  if (!isTrialUser) {
    const cleared = { ...EMPTY, lastCheckAt: previous.lastCheckAt, licenseEndDate };
    writeTrialContext(cleared);
    return cleared;
  }

  const appDeactivateDate = num(data?.app_deactivate_date);
  const next = {
    isTrialUser: true,
    appDeactivateDate,
    appDeleteProgressDate: num(data?.app_delete_progress_date),
    // iOS: `if (deactivatedInterval <= currentInterval) Is7DaysFinished = YES`.
    // A missing/zero date is NOT treated as finished — zero would compare as
    // long past and would announce an expiry the server never reported.
    isFinished: appDeactivateDate > 0 && appDeactivateDate <= now / 1000,
    lastCheckAt: previous.lastCheckAt,
    licenseEndDate,
  };
  writeTrialContext(next);
  return next;
}

/**
 * The trial half of iOS's hourly ping (`KFEPingServerManager.m:191-212`),
 * ported onto touch-base (Brief 194 made touch-base the web's heartbeat).
 *
 * iOS's logic verbatim: **only when the learner is currently on trial** does it
 * look at `is_trial_user`, and only a `false` does anything — it clears the
 * whole context, which is what stops the trial messages. A `true` on the ping
 * is a no-op there and is a no-op here (the dates come from login, not from the
 * ping), so this never *starts* a trial and never extends one.
 *
 * Brief 016 criterion 4 is exactly this: once touch-base reports
 * `is_trial_user: false`, no trial message is shown again.
 *
 * @param {any} data a successful touch-base response's `data` block
 * @returns {boolean} whether the context was cleared
 */
export function applyTouchBaseTrialState(data) {
  const ctx = getTrialContext();
  if (!ctx.isTrialUser) return false;
  // Absent field: say nothing. A touch-base response that simply does not
  // carry `is_trial_user` must not be read as "the trial is over" — that
  // would end a live trial on the first server deploy that renamed a field.
  if (data == null || !("is_trial_user" in data)) return false;
  if (truthy(data.is_trial_user)) return false;

  writeTrialContext({ ...EMPTY, lastCheckAt: ctx.lastCheckAt, licenseEndDate: ctx.licenseEndDate });
  return true;
}

/**
 * iOS's `boolValue`, which is true for `true`, `1` and the string `"1"`/`"true"`
 * alike. The CRM has answered booleans as both JSON `true` and `1` elsewhere
 * (crm-client's success check tolerates the same pair), so this is tolerance,
 * not guesswork.
 * @param {any} v
 * @returns {boolean}
 */
function truthy(v) {
  return v === true || v === 1 || v === "1" || v === "true";
}

/** @typedef {{ kind: "welcome", date: string } | { kind: "endsIn", days: number } | { kind: "expired" }} TrialMessage */

/**
 * `+[KFE7DaysTrialMgr appDidActivate]` (KFE7DaysTrialMgr.m:93-181), ported as
 * a decision rather than a presentation.
 *
 * Returns the message that is due NOW, or null for "say nothing", and writes
 * back the state iOS writes back (`Is7DaysFinished`, `LastCheckTimeStamp`), so
 * the once-ever and once-a-day guarantees survive a reload. Calling it twice in
 * the same day therefore yields a message the first time and null the second —
 * which is the whole point of iOS's throttle.
 *
 * iOS's order, preserved:
 *   1. not on trial                         → nothing
 *   2. already finished                     → the expired message (every time;
 *      iOS re-shows it on each activation until the learner acts)
 *   3. shown something already today        → nothing  (`kIntervalShowingAlerw` = 1)
 *   4. the trial end has passed             → mark finished, the expired message
 *   5. first message ever (`lastCheck` = 0) → the welcome-until-date message
 *   6. otherwise                            → "ends in N days"
 *
 * Step 4 is where iOS ALSO deletes the learner's progress after 10 days. Not
 * ported — see the module header.
 *
 * @param {{ now?: number }} [opts] `now` in MILLISECONDS (client clock)
 * @returns {TrialMessage | null}
 */
export function decideTrialMessage({ now = Date.now() } = {}) {
  const ctx = getTrialContext();
  if (!ctx.isTrialUser) return null;
  if (ctx.isFinished) return { kind: "expired" };

  const nowSeconds = now / 1000;

  // iOS throttles on the CALENDAR DAY, not on elapsed hours: it formats both
  // stamps as yyyyMMdd integers and returns when the difference is < 1. So two
  // activations an hour apart either side of midnight DO both speak, and two
  // twelve hours apart within one day do not. Mirrored with local-date keys
  // rather than a 24h window, because a 24h window is a different rule.
  if (ctx.lastCheckAt > 0 && dayKey(ctx.lastCheckAt * 1000) === dayKey(now)) return null;

  if (ctx.appDeactivateDate > 0 && ctx.appDeactivateDate <= nowSeconds) {
    writeTrialContext({ ...ctx, isFinished: true, lastCheckAt: nowSeconds });
    return { kind: "expired" };
  }

  // A trial with no end date cannot be counted down. iOS would divide by a
  // zero date and announce a wildly negative day count; saying nothing is the
  // honest answer, and the next touch-base or login will carry the date.
  if (ctx.appDeactivateDate <= 0) return null;

  writeTrialContext({ ...ctx, lastCheckAt: nowSeconds });

  if (ctx.lastCheckAt === 0) {
    return { kind: "welcome", date: isoDate(ctx.appDeactivateDate * 1000) };
  }
  // `(int) ceil(remainingInterval/60/60/24)` — iOS rounds UP, so the last
  // part-day of a trial reads as "1 day", never "0 days".
  const days = Math.ceil((ctx.appDeactivateDate - nowSeconds) / 86400);
  return { kind: "endsIn", days };
}

/**
 * iOS's `yyyMMdd` throttle key (note its own typo — three `y`s, which
 * NSDateFormatter still renders as a four-digit year). LOCAL time, as
 * NSDateFormatter's default is the device's time zone.
 * @param {number} ms
 * @returns {string}
 */
function dayKey(ms) {
  const d = new Date(ms);
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

/**
 * The welcome message's date, in iOS's own `yyyy-MM-dd` (the `endFormatter` in
 * `appDidActivate`). LOCAL time, for the same reason as above.
 * @param {number} ms
 * @returns {string}
 */
function isoDate(ms) {
  const d = new Date(ms);
  const pad = (/** @type {number} */ n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
